
import { getData } from './common.service';
import { Geolocation } from '@capacitor/geolocation';

export class GeolocationService {
    async getCurrentPosition() {
        try {
            const coordinates = await Geolocation.getCurrentPosition({
                timeout: 30000, // 30 seconds timeout
                enableHighAccuracy: true,
                maximumAge: 300000 // 5 minutes - accept cached positions
            });
            return coordinates;
        } catch (err: any) {
            console.log('Geolocation error:', err);
            
            // Provide more specific error information
            let enhancedError = { ...err };
            
            if (err.code === 1) {
                enhancedError.message = 'Location permission denied. Please enable location access in your device settings.';
                enhancedError.userAction = 'Go to Settings > Privacy & Security > Location Services and enable for this app.';
            } else if (err.code === 2) {
                enhancedError.message = 'Location unavailable. This usually means GPS signal is weak or blocked.';
                enhancedError.userAction = 'Try moving to an open area, near a window, or outdoors. Check if airplane mode is off.';
            } else if (err.code === 3) {
                enhancedError.message = 'Location request timed out. GPS signal might be weak.';
                enhancedError.userAction = 'Try again in a few seconds, or move to an area with better GPS reception.';
            }
            
            throw enhancedError;
        }
    }

    async checkPermissions() {
        try {
            const permission = await Geolocation.checkPermissions();
            return permission;
        } catch (err) {
            console.log('Permission check failed:', err);
            return { location: 'denied' };
        }
    }

    async requestPermissions() {
        try {
            const permission = await Geolocation.requestPermissions();
            return permission;
        } catch (err) {
            console.log('Permission request failed:', err);
            return { location: 'denied' };
        }
    }

    async isGPSEnabled(): Promise<boolean> {
        try {
            const position = await this.getCurrentPosition();
            return !!position?.coords;
        } catch (error: any) {
            console.log('GPS check failed:', error);
            
            // Provide more specific error handling
            if (error.code === 1) {
                // Permission denied - GPS is not accessible
                console.log('Location permission denied - user needs to grant access');
                return false;
            } else if (error.code === 2) {
                // Position unavailable - GPS might be enabled but signal is weak
                console.log('GPS signal weak or blocked - but GPS might be enabled');
                return true; // Don't disable GPS on weak signal
            } else if (error.code === 3) {
                // Timeout - GPS might still be enabled but signal is weak
                console.log('GPS timeout - signal might be weak but GPS is enabled');
                return true; // Don't disable GPS on timeout
            }
            
            return false;
        }
    }
}

export async function getReverseGecodingAddress(latitude: number, longitude: number) {
    try {
        const response = await getData(`/regions/address?lat=${latitude}&lon=${longitude}`, {
            basePath: process.env.REACT_APP_CORE_API_BASEPATH,
            sendTokenInHeader: true,
        });
        
        const data = await response.json();
        
        // Transform the response to match the expected format
        return {
            address: {
                village: data.village || '',
                subDistrict: data.subDistrict || '',
                district: data.district || '',
                state: data.state || '',
                country: data.country || ''
            },
            display_name: `${data.village || ''}, ${data.subDistrict || ''}, ${data.district || ''}, ${data.state || ''}, ${data.country || ''}`
        };
        
    } catch (error) {
        console.log('Fyllo-core reverse geocoding failed:', error);
        // Fallback to basic coordinates
        return {
            address: {
                village: '',
                hamlet: '',
                town: '',
                county: '',
                city: '',
                state: '',
                country: 'India'
            },
            display_name: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
        };
    }
}

export async function getElevation(latitude: number, longitude: number) {
    try {
        const res = await getData(`/lookup?locations=${latitude},${longitude}`, {
            basePath: process.env.REACT_APP_OPENELEVATION_BASEPATH
        });
        return await res.json();
    } catch (error) {
        console.log('Elevation lookup failed:', error);
        return { results: [] };
    }
}